import equipmentApi from '@/pages/Equipment/equipment.api'
import { IEquipmentPassport, IPrevEquipmentPassportVersion } from '@/pages/Equipment/equipment.types'
import usePassportSchema from '@/pages/MDM/hooks/usePassprotSchema'
import useHttpLoader from '@/shared/hooks/useHttpLoader'
import { TDropdownOption } from '@/shared/Inputs/DropdownInput/dropdown.types'
import timeUtils from '@/utils/time.utils'
import { useState, useEffect, useMemo } from 'react'
import passportVersionsConstants from '../passportVersions.constants'

interface IProps {
  passport: IEquipmentPassport
}

export type TSelectedСomparableVersionIds = {
  left: IPrevEquipmentPassportVersion['id']
  right: IPrevEquipmentPassportVersion['id']
}

const useEquipmentPassportVersionsHistoryCtrl = (props: IProps) => {
  const [isVersionComparisonEnabled, setIsVersionComparisonEnabled] = useState(false)
  const passportSchema = usePassportSchema(props.passport?.passportId)
  const { wait, loading } = useHttpLoader()
  const [selectedVersionId, setSelectedVersionId] = useState<IPrevEquipmentPassportVersion['id']>(
    passportVersionsConstants.currentVersionId
  )
  const [selectedСomparableVersionIds, setSelectedСomparableVersionIds] = useState<TSelectedСomparableVersionIds>({
    left: passportVersionsConstants.currentVersionId,
    right: passportVersionsConstants.currentVersionId,
  })
  const [previousPassportVersions, setPreviousPassportVersions] = useState<IPrevEquipmentPassportVersion[]>([])
  const [isChangedOnly, setIsChangedOnly] = useState<boolean>(false)

  useEffect(() => {
    if (!props.passport || !passportSchema) return
    wait(equipmentApi.getPreviousPassportVersions(props.passport.id), (resp) => {
      if (resp.status === 'success') {
        const filteredPreviousPassportVersions = resp.body.filter((ver) =>
          timeUtils.isBefore(passportSchema.updatedAt, ver.createdAt)
        )
        setPreviousPassportVersions(filteredPreviousPassportVersions)
        if (filteredPreviousPassportVersions.length > 0) {
          setSelectedСomparableVersionIds({
            left: filteredPreviousPassportVersions[0].id,
            right: passportVersionsConstants.currentVersionId,
          })
          setSelectedVersionId(filteredPreviousPassportVersions[0].id)
        }
      }
    })
  }, [props.passport, passportSchema])

  const getPreviousPassportVersionOptionName = (
    createdAt: IPrevEquipmentPassportVersion['createdAt'],
    replacedAt: IPrevEquipmentPassportVersion['replacedAt']
  ) => {
    const createdAtFormatDate = timeUtils.formatDate(createdAt, 'dd.MM.yyyy')
    const replacedAtFormatDate = timeUtils.formatDate(replacedAt, 'dd.MM.yyyy')
    return `${createdAtFormatDate} - ${replacedAtFormatDate}`
  }

  // passportVersionsConstants.currentVersionId для актуальной версии паспорта
  const prevPassportVersionOptions: TDropdownOption[] = useMemo(() => {
    const currentVersionOption = { value: passportVersionsConstants.currentVersionId, title: 'Актуальная версия' }
    if (!previousPassportVersions) return [currentVersionOption]
    const res = previousPassportVersions.map((ver) => ({
      title: getPreviousPassportVersionOptionName(ver.createdAt, ver.replacedAt),
      value: ver.id,
    }))
    res.unshift(currentVersionOption)
    return res
  }, [previousPassportVersions])

  const handleChangeSelecteСomparableVersionIds = (value: number, name: string) => {
    setSelectedСomparableVersionIds((prev) => ({ ...prev, [name]: value }))
  }

  return {
    data: {
      loading,
      previousPassportVersions,
      selectedVersionId,
      isVersionComparisonEnabled,
      prevPassportVersionOptions,
      selectedСomparableVersionIds,
      isChangedOnly,
    },
    handlers: {
      handleSelectVersionId: (value: number) => setSelectedVersionId(value),
      handleChangeSelecteСomparableVersionIds,
      setIsVersionComparisonEnabled,
      handleChangeOnly: (value: boolean) => setIsChangedOnly(value),
    },
  }
}

export default useEquipmentPassportVersionsHistoryCtrl
